import React, { useCallback, useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { ENDPOINTS } from 'features/quick-entry/constants/endpoints';
import { useShowAlert } from 'hooks';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FileManager from 'UI/components/organisms/FileManager';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage } from 'UI/utils';

import { QuickEntryTextField } from '../QuickEntryTextField';

import {
  COPIES,
  DEFAULT_FORM_VALUES,
  DELETE_API_VERSION,
  FORM_FIELDS,
  UPLOAD_CATEGORIES
} from './CandidateQuickEntryDrawer.constants';
import { useStyles } from './CandidateQuickEntryDrawer.styled';
import { buildFormParams } from './CandidateQuickEntryDrawer.utils';

export const CandidateQuickEntryDrawer = ({ onClose = () => {}, quickEntryId = null }) => {
  const classes = useStyles();
  const { showAlert } = useShowAlert();
  const [isSaving, setIsSaving] = useState(false);
  const form = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onSubmit',
    reValidateMode: 'onBlur'
  });
  const { handleSubmit, register, setValue } = form;

  useEffect(() => {
    register({ name: FORM_FIELDS.files });
  }, [register]);

  const handleFilesChanged = useCallback(
    data => {
      const attachmentsChanged = flatMap(map(data, 'files'));

      setValue(FORM_FIELDS.files, attachmentsChanged);
    },
    [setValue]
  );

  const onSubmit = useCallback(
    async formData => {
      try {
        setIsSaving(true);
        const finalParams = buildFormParams(formData);
        const request = quickEntryId ? SECONDARY_API_CLIENT.put : SECONDARY_API_CLIENT.post;
        const action = quickEntryId ? 'updated' : 'created';
        const endpoint = quickEntryId
          ? `${ENDPOINTS.quickEntriesById.replace(':id', quickEntryId)}`
          : ENDPOINTS.quickEntries;
        const response = await request(endpoint, finalParams);

        if (
          [HTTPStatusCodes.Ok, HTTPStatusCodes.Created, HTTPStatusCodes.NoContent].includes(
            response.status
          )
        ) {
          showAlert({
            severity: 'success',
            title: COPIES.title,
            body: strings.formatString(COPIES.alert.success, {
              action
            })
          });

          setIsSaving(false);
          onClose();
        }
      } catch (error) {
        setIsSaving(false);
        showAlert({
          body: getErrorMessage(error, COPIES.alert.error),
          title: COPIES.title,
          severity: 'error'
        });
      }
    },
    [onClose, quickEntryId, showAlert]
  );

  const buttonDefinitions = [
    {
      props: {
        text: COPIES.actions.cancel,
        variant: 'text',
        onClick: onClose
      }
    },
    {
      mode: 'save',
      props: {
        isSaving,
        onClick: handleSubmit(onSubmit),
        text: COPIES.actions.saveEntry,
        variant: 'outlined'
      }
    },
    {
      props: {
        disabled: isSaving,
        text: COPIES.actions.continueToDataSheet,
        variant: 'contained',
        onClick: () => console.log('WIP. Continue to Data Sheet')
      }
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: COPIES.title }}
      FPDrawerProps={{
        open: true,
        onClose,
        classes: { paper: classes.drawerPaper }
      }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
      hasPaddingContent={false}
      onClose={onClose}
    >
      <FormContext {...form}>
        <div className={classes.content}>
          <QuickEntryTextField
            label={COPIES.fields.name}
            name={FORM_FIELDS.name}
            required
            rows={1}
          />
          <QuickEntryTextField
            label={COPIES.fields.notes}
            multiline
            name={FORM_FIELDS.notes}
            rows={12}
          />
          <div className={classes.uploads}>
            <FileManager
              apiVersionForDeleting={DELETE_API_VERSION}
              defaultCategories={UPLOAD_CATEGORIES}
              filesEndpoint={Endpoints.Files}
              isNewEntity
              layoutInColumns
              module="quickEntryCandidate"
              onAttachmentsChanged={handleFilesChanged}
              showItemDate
              showItemMenu
              viewItemOnClick
            />
          </div>
        </div>
      </FormContext>
    </FPDrawer>
  );
};
